/** @jsxImportSource @emotion/react */

import { Accordion, AccordionContent, AccordionItem } from '@/components/ui/accordion'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import WorkspaceSelector from '@app/modules/Risk/Profile/components/WorkspaceSelector'
import { useApplyWorkspace } from '@app/modules/Risk/Workspaces/hooks/useApplyWorkspace'
import { activeWorkspaceAtom } from '@app/risk-migration/atoms/riskMap.atoms'
import { css } from '@emotion/react'
import { Box, Button, Typography, useTheme } from '@mui/material'
import { useAtomValue } from 'jotai'
import { SaveWorkspaceButton } from './SaveWorkspaceButton'
import { WorkspaceSetSelects } from './WorkspaceSetSelects'
import { useNavigate } from 'react-router-dom'
import { Button as RButton } from '@/components/ui/button'
import { ChevronDown, FolderIcon, FolderOpen } from 'lucide-react'

export const ActiveWorkspaceHeader = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const activeWorkspace = useAtomValue(activeWorkspaceAtom)
  const { useFeatureFlag } = useFeatureFlags()
  const { applyWorkspace } = useApplyWorkspace()
  const workspaceV2Enabled = useFeatureFlag(FEATURE_FLAGS.WORKSPACE_V2)

  return (
    <Box
      p={'12px 12px 12px 16px'}
      borderBottom={`1px solid ${theme.palette.uintel.main}20`}
      display="flex"
      justifyContent="space-between"
      gap={1}
    >
      {workspaceV2Enabled ? (
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="workspace" className="flex flex-col gap-1 border-b-0 w-full">
            <div className="flex justify-between w-full">
              <Typography variant="h6" component="div" color={'primary'} noWrap minWidth={0}>
                <strong>{activeWorkspace ? activeWorkspace.name : 'Active layers'}</strong>
              </Typography>
              <div className="flex gap-2 px-1 mb-1">
                <SaveWorkspaceButton />

                {/* Primitive, so the down arrow doesn't show */}
                <AccordionPrimitive.Trigger asChild>
                  <RButton variant={'default'} className="group">
                    <FolderIcon />
                    Workspaces
                    <ChevronDown className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </RButton>
                </AccordionPrimitive.Trigger>
              </div>
            </div>
            <AccordionContent className="pb-0">
              <div className="mt-1 px-[2px]">
                <div className="flex w-full gap-2">
                  <WorkspaceSelector
                    className="w-60"
                    location="riskMap"
                    value={activeWorkspace}
                    onSelect={applyWorkspace}
                  />
                </div>
                <WorkspaceSetSelects />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : (
        <Button
          onClick={() => navigate('workspaces/create')}
          variant="text"
          size="small"
          startIcon={
            <Box
              sx={{
                backgroundColor: theme.palette.blue.main,
                color: theme.palette.blue.contrastText,
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
              }}
            >
              <FolderOpen size={16} />
            </Box>
          }
          color={'blue'}
          css={css`
            align-self: flex-end;
            margin-left: 8px;
            font-size: 14px;
          `}
        >
          Workspaces
        </Button>
      )}
    </Box>
  )
}
